import {
  AppointmentStatus,
  IAppointment,
  IPatientRecall,
  IRecallSettings,
  StatusChange,
} from '@principle-theorem/principle-core/interfaces';
import { isSameRef, toMomentTz, WithRef } from '@principle-theorem/shared';
import { TimezoneResolver } from '../../timezone';
import { Recalls } from './recalls';

export class PatientRecallUpdateCheck {
  status: StatusChange<AppointmentStatus>;

  constructor(
    public settings: IRecallSettings,
    public recall: IPatientRecall,
    public appointment: WithRef<IAppointment>,
    public appointmentBefore?: WithRef<IAppointment>
  ) {
    this.status = new StatusChange(
      this.appointmentBefore?.status,
      this.appointment.status
    );
  }

  async needsUpdate(): Promise<boolean> {
    const isRecallAppointment = await Recalls.isRecallAppointment(
      this.settings,
      this.appointment
    );
    if (!isRecallAppointment && this._isReferencedAsRecallAppointment()) {
      return true;
    }
    if (!isRecallAppointment) {
      return false;
    }

    if (
      this.status.changedTo(AppointmentStatus.Unscheduled) ||
      this.status.changedTo(AppointmentStatus.Scheduled) ||
      this.status.changedFromOrTo(AppointmentStatus.Complete) ||
      this.status.changedFromOrTo(AppointmentStatus.Cancelled)
    ) {
      return true;
    }

    const wasRescheduled = await this._movedToDifferentDay(
      this.appointment,
      this.appointmentBefore
    );
    if (wasRescheduled) {
      return true;
    }

    return false;
  }

  private _isReferencedAsRecallAppointment(): boolean {
    return (
      isSameRef(this.appointment.ref, this.recall.lastRecallAppointment) ||
      isSameRef(this.appointment.ref, this.recall.nextRecallAppointment)
    );
  }

  private async _movedToDifferentDay(
    appointment: WithRef<IAppointment>,
    beforeAppointment?: WithRef<IAppointment>
  ): Promise<boolean> {
    if (appointment.status !== AppointmentStatus.Scheduled) {
      return false;
    }
    if (!beforeAppointment?.event?.from || !appointment.event?.from) {
      return false;
    }
    const timezone = await TimezoneResolver.fromEvent(appointment);
    const before = toMomentTz(beforeAppointment.event.from, timezone);
    const after = toMomentTz(appointment.event.from, timezone);
    return !before.isSame(after, 'day');
  }
}
